import { WorkflowDialogWidth, WorkflowType } from "@common/ADAPT.Common.Model/embed/workflow";
import { WorkflowStepGuidancePosition } from "@common/ADAPT.Common.Model/embed/workflow-step";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { TeamType, TeamTypeMetadata } from "@common/ADAPT.Common.Model/organisation/team-type";
import { TeamTypeCode, TeamTypeCodeMetadata } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { ICreateTeamBindingModel } from "@org-common/lib/teams/create-team-binding-model.interface";
import { buildLocalWorkflow } from "@org-common/lib/workflow/workflow.interface";

export interface IAddTeamWorkflowRunData {
    team: Team;
    entitiesToConfirm: IBreezeEntity[];
    createTeamBindingModel: ICreateTeamBindingModel;
    hasCustomisedFeatures?: boolean;
    defaultLocation?: TeamLocation;
}

function buildAddTeamWorkflow(hasOrganisationConfiguration = false) {
    let ordinal = 0;
    return buildLocalWorkflow<IAddTeamWorkflowRunData>({
        name: "Add team",
        workflowId: "AddTeamLocal",
        type: WorkflowType.Workflow,
        dialogWidth: WorkflowDialogWidth.Large,
        private: true,
        isStateless: true,
        showBreadcrumbs: false,
        showProgress: true,
        showProgressTitles: true,
        steps: [{
            name: "Details",
            icon: "fal fa-memo-circle-info",
            ordinal: ordinal++,
            canSkip: false,
            componentSelector: "adapt-add-team-details-step",
            articleSlug: ImplementationKitArticle.AddTeamDetails,
            articlePosition: WorkflowStepGuidancePosition.Left,
        }, {
            name: "Members",
            icon: "fal fa-users",
            ordinal: ordinal++,
            allowBack: true,
            canSkip: true,
            componentSelector: "adapt-add-team-members-step",
        }, ...(hasOrganisationConfiguration ? [{
            name: "Appearance",
            icon: "fal fa-swatchbook",
            ordinal: ordinal++,
            allowBack: true,
            canSkip: true,
            componentSelector: "adapt-add-team-customise-step",
            simpleHtml: `<p>Customise the appearance of your group.
            Your colours and icon will be visible on the organisation map, and whenever the group is linked to.</p>`,
        }] : []), {
            name: "Features",
            icon: "fal fa-circle-star",
            ordinal: ordinal++,
            allowBack: true,
            canSkip: true,
            componentSelector: "adapt-add-team-features-step",
            simpleHtml: `<p>Enable or disable features for the group. Only features that are enabled in your organisation are shown here.</p>`,
        }, {
            name: "Confirm",
            icon: "fal fa-check-double",
            ordinal: ordinal++,
            allowBack: true,
            canSkip: false,
            componentSelector: "adapt-add-team-confirm-step",
            simpleHtml: `<p>Please review the details for the new group before creating it. Any details can be edited later via the group's configuration page.</p>`,
            workflowStepNextText: "Create group",
            workflowStepNextIcon: "fal fa-fw fa-plus-square",
        }],
    });
}

export const addTeamWorkflow = buildAddTeamWorkflow(false);
export const addTeamWorkflowWithOrganisationConfigurePermission = buildAddTeamWorkflow(true);

export function initialiseAddTeamWorkflowRunData(runData: Partial<IAddTeamWorkflowRunData>) {
    if (!runData || !runData.team) {
        throw new Error("Team needs to be provided to the workflow");
    }

    if (!runData.entitiesToConfirm) {
        runData.entitiesToConfirm = [];
    }

    if (!runData.createTeamBindingModel) {
        runData.createTeamBindingModel = {
            organisationId: runData.team.organisationId,
            name: runData.team.name,
            teamLeaderPersonId: runData.team.teamLeaderPerson?.personId,
            teamType: runData.team.teamType,
            isPrivate: runData.team.isPrivate,
            parentTeamIds: [],
            parentZones: [],
            childTeamIds: [],
            memberPersonIds: [],
            teamFeatures: [],
            customData: runData.team.customData,
            teamTypeConfigurationId: runData.team.teamTypeConfigurationId,
        };
    }

    if (runData.hasCustomisedFeatures === undefined) {
        runData.hasCustomisedFeatures = false;
        setTeamDefaultFeatures(runData as IAddTeamWorkflowRunData);
    }

    return runData as IAddTeamWorkflowRunData;
}

export function getTeamDefaultFeatures(teamType: TeamType, teamTypeConfiguration: TeamTypeConfiguration) {
    // Leadership and Owner team types have different feature sets
    if (teamType !== TeamType.Standard) {
        return [...TeamTypeMetadata.DefaultFeatures[teamType]];
    }

    // KF & VS team type codes have different feature sets.
    // Team & Leadership are just the standard defaults.
    if (teamTypeConfiguration.code) {
        return [...TeamTypeCodeMetadata.DefaultFeatures[teamTypeConfiguration.code]];
    }

    return [...TeamTypeCodeMetadata.DefaultFeatures[TeamTypeCode.Team]];
}

export function setTeamDefaultFeatures(runData: IAddTeamWorkflowRunData) {
    runData.createTeamBindingModel.teamFeatures = getTeamDefaultFeatures(runData.team.teamType, runData.team.teamTypeConfiguration);
    return runData;
}
